import React, { useCallback, useMemo, useRef, useState } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { CHARTER_BUNDLE_DURATION_OPTIONS } from 'constants/charterFlight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { FlightsClassTypes } from 'constants/flight'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { parseAsBoolean, useQueryState } from 'nuqs'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { useIntl } from 'react-intl'

interface StateProps {
  offer: App.HotelOffer
  duration: number
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  charterBundleRoute: App.CharterBundleRoute
}

interface Props {
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  onClose?: () => void
}

/**
 * Charter Bundle Calendar Modal for Mobile
 * Fetches its own data using useOfferCharterCalendarData hook
 * Follows the pattern of OfferCalendarSidebarDatePicker - dispatches dates internally
 */
function FlashMobileStickyHeaderFiltersCharterCalendarModal(props: Props & StateProps) {
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    charterBundleRoute,
    fareClass,
    priceMode,
    onDurationChange,
    onClose,
  } = props

  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dispatch = useOfferPageDispatch()
  const modalBodyRef = useRef<HTMLDivElement | null>(null)

  // Read luxPlus state from URL (same as parent component)
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(true),
  )

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: rooms.length === 0,
  })

  // Filter dateRangesByMonth by selected nights
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, duration),
    [allDateRangesByMonth, duration],
  )

  // Manage selected month state internally
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM'),
  )

  // Fetch charter calendar data
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    charterBundleRoute,
  })

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Like OfferCalendarSidebarDatePicker, dispatch dates internally
    // Use checkIn and checkOut (inner dates) for hotel queries
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: option.checkIn,
      checkOut: option.checkOut,
    })
    dispatch({ type: OfferPageActions.closeHeaderFilter })
    onClose?.()
    modalElement.resolve()
  }, [dispatch, onClose, modalElement])

  const handleClose = useCallback(() => {
    onClose?.()
    modalElement.resolve()
  }, [modalElement, onClose])

  const selectedOptionId = useMemo(() => {
    return checkInDate ? `${checkInDate}-${duration}n` : undefined
  }, [checkInDate, duration])

  const handleFareClassChange = useCallback((selectedClass: FlightsClassTypes) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
  }, [dispatch])

  // Filter duration options to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return CHARTER_BUNDLE_DURATION_OPTIONS.filter(option => {
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [offer.packages])
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
  }, [offer.campaignTags, rooms])

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={filteredDurationOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={fareClass}
      onFareClassChange={handleFareClassChange}
      availableFareClasses={charterFareClassPolicy.allowedFareClasses}
    />
  )

  const modalTitle = intl.formatMessage({
    id: 'yCqcj91896',
    defaultMessage: 'Select your trip dates',
    description: 'Title for charter calendar modal to select trip dates',
  })

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={handleClose}
      />
      <ModalBody ref={modalBodyRef}>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="offer"
            offer={offer}
            headerActions={actionsElement}
            scrollContainerRef={modalBodyRef}
          />
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer!,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  airport: state.airport,
  charterBundleRoute: state.charterBundleRoute!,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
}), FlashMobileStickyHeaderFiltersCharterCalendarModal)
